import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import {
  ErrorIssue,
  ErrorIssueFilters,
  ErrorIssueStatus,
  ErrorTrackingKey,
  ErrorTrackingStats,
  SourceMapArtifact,
} from 'src/app/models/error-tracking.model';
import { Project } from 'src/app/models/project.model';
import { ErrorTrackingService } from 'src/app/services/error-tracking.service';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { ToastService } from 'src/app/services/toast.service';

type Tab = 'issues' | 'setup';

@Component({
  selector: 'app-project-errors',
  templateUrl: './project-errors.component.html',
  styleUrls: ['./project-errors.component.scss'],
  standalone: false
})
export class ProjectErrorsPageComponent implements OnInit {

  projectId: string = null;
  project: Project | null = null;

  tab: Tab = 'issues';

  issues: ErrorIssue[] = [];
  issueCount = 0;
  stats: ErrorTrackingStats | null = null;
  keys: ErrorTrackingKey[] = [];

  filters: ErrorIssueFilters = { status: 'unresolved', sort: 'last_seen', q: '', environment: '', page: 1 };

  loading = true;
  loadingIssues = false;
  error = false;

  snippetPlatform: 'angular' | 'django' = 'angular';
  copiedKey: string | null = null;
  showSecret = false;

  sourceMaps: SourceMapArtifact[] = [];
  uploadRelease = '';
  uploading = false;

  readonly pageSize = 10;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private errorTrackingService: ErrorTrackingService,
    private projectService: ProjectManagementService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');

    try {
      const [project, stats] = await Promise.all([
        lastValueFrom(this.projectService.getProject(this.projectId)),
        lastValueFrom(this.errorTrackingService.getStats(this.projectId)),
      ]);
      this.project = project;
      this.stats = stats;
      await this.loadIssues();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  async loadIssues() {
    this.loadingIssues = true;
    this.cdr.markForCheck();

    try {
      const response = await lastValueFrom(
        this.errorTrackingService.getIssues(this.projectId, this.filters)
      );
      this.issues = response.results;
      this.issueCount = response.count;
    } catch {
      this.error = true;
    } finally {
      this.loadingIssues = false;
      this.cdr.markForCheck();
    }
  }

  async selectTab(tab: Tab) {
    this.tab = tab;
    if (tab === 'setup' && this.keys.length === 0) {
      await this.loadKeys();
      await this.loadSourceMaps();
    }
  }

  async loadSourceMaps() {
    try {
      this.sourceMaps = await lastValueFrom(this.errorTrackingService.getSourceMaps(this.projectId));
    } catch {
      this.toastService.show('Could not load the source maps.', 'error');
    } finally {
      this.cdr.markForCheck();
    }
  }

  async onSourceMapSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) {
      return;
    }

    if (!this.uploadRelease.trim()) {
      this.toastService.show('Enter the release these maps belong to first.', 'error');
      input.value = '';
      return;
    }

    this.uploading = true;
    this.cdr.markForCheck();

    let uploaded = 0;
    for (const file of files) {
      try {
        await lastValueFrom(
          this.errorTrackingService.uploadSourceMap(this.projectId, this.uploadRelease.trim(), file)
        );
        uploaded += 1;
      } catch {
        this.toastService.show(`Could not upload ${file.name}.`, 'error');
      }
    }

    if (uploaded) {
      this.toastService.show(`Uploaded ${uploaded} source map${uploaded === 1 ? '' : 's'}.`);
    }

    input.value = '';
    this.uploading = false;
    await this.loadSourceMaps();
  }

  async deleteSourceMap(artifact: SourceMapArtifact) {
    if (!confirm(`Delete the source map for ${artifact.name}?`)) {
      return;
    }

    try {
      await lastValueFrom(this.errorTrackingService.deleteSourceMap(this.projectId, artifact.uuid));
      await this.loadSourceMaps();
    } catch {
      this.toastService.show('Could not delete the source map.', 'error');
    }
  }

  formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} kB`;
    return `${bytes} B`;
  }

  get uploadToken(): string {
    return this.keys.find(k => k.is_active)?.secret_key ?? '<upload token>';
  }

  get sourceMapUploadSnippet(): string {
    return `# in CI, after building with source maps
for map in dist/*.js.map; do
  curl -sS -X POST '${this.environmentBaseUrl}/api/v1/error-tracking/projects/${this.projectId}/sourcemaps' \\
    -H 'X-PulseWatch-Token: ${this.uploadToken}' \\
    -F "release=$RELEASE" \\
    -F "file=@$map"
done

# then delete the maps so they are not served to the public
rm dist/*.js.map`;
  }

  get environmentBaseUrl(): string {
    return this.keys[0]?.ingest_url?.split('/api/v1/')[0] ?? '';
  }

  async loadKeys() {
    try {
      this.keys = await lastValueFrom(this.errorTrackingService.getKeys(this.projectId));
    } catch {
      this.toastService.show('Could not load the ingest keys.', 'error');
    } finally {
      this.cdr.markForCheck();
    }
  }

  async setStatusFilter(status: ErrorIssueStatus | '') {
    this.filters = { ...this.filters, status, page: 1 };
    await this.loadIssues();
  }

  async onSearchChange(value: string) {
    this.filters = { ...this.filters, q: value, page: 1 };
    await this.loadIssues();
  }

  async onEnvironmentChange(value: string) {
    this.filters = { ...this.filters, environment: value, page: 1 };
    await this.loadIssues();
  }

  async onSortChange(value: string) {
    this.filters = { ...this.filters, sort: value as ErrorIssueFilters['sort'], page: 1 };
    await this.loadIssues();
  }

  async goToPage(page: number) {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.filters = { ...this.filters, page };
    await this.loadIssues();
  }

  get totalPages(): number {
    return Math.max(Math.ceil(this.issueCount / this.pageSize), 1);
  }

  get currentPage(): number {
    return this.filters.page ?? 1;
  }

  openIssue(issue: ErrorIssue) {
    this.router.navigate(['/main', 'project', this.projectId, 'errors', issue.uuid]);
  }

  goBack() {
    this.router.navigate(['/main', 'project', this.projectId]);
  }

  async copy(text: string, keyUuid: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.copiedKey = keyUuid;
      setTimeout(() => {
        this.copiedKey = null;
        this.cdr.markForCheck();
      }, 2000);
    } catch {
      this.toastService.show('Could not copy to clipboard.', 'error');
    }
    this.cdr.markForCheck();
  }

  // -- chart --------------------------------------------------------------

  readonly chartHeight = 48;

  get maxDailyCount(): number {
    if (!this.stats?.daily_events?.length) return 1;
    return Math.max(...this.stats.daily_events.map(d => d.count), 1);
  }

  barHeight(count: number): number {
    return Math.max((count / this.maxDailyCount) * this.chartHeight, 2);
  }

  // -- formatting ---------------------------------------------------------

  relativeTime(value: string): string {
    const diffSeconds = (Date.now() - new Date(value).getTime()) / 1000;

    if (diffSeconds < 60) return 'just now';
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    if (diffSeconds < 2592000) return `${Math.floor(diffSeconds / 86400)}d ago`;
    return new Date(value).toLocaleDateString();
  }

  formatDay(value: string): string {
    return new Date(value + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' });
  }

  ingestUrlFor(platform: 'angular' | 'django'): string {
    const match = this.keys.find(k => k.platform === platform && k.is_active);
    return (match ?? this.keys[0])?.ingest_url ?? 'https://…/api/v1/error-tracking/ingest/<public_key>';
  }

  get angularSnippet(): string {
    return `// app.module.ts (or providers in bootstrapApplication)
import { PulseWatchModule } from './pulsewatch';

@NgModule({
  imports: [
    PulseWatchModule.forRoot({
      ingestUrl: '${this.ingestUrlFor('angular')}',
      environment: environment.production ? 'production' : 'development',
      // Must match the release you upload source maps under.
      release: '1.0.0',
      enabled: environment.production,
    }),
  ],
})
export class AppModule {}`;
  }

  get djangoSnippet(): string {
    return `# settings.py
PULSEWATCH = {
    "ingest_url": "${this.ingestUrlFor('django')}",
    "environment": "production",
    "release": "1.0.0",
    "project_root": BASE_DIR,

    # Tracing: the share of requests recorded with their queries and timings.
    "traces_sample_rate": 0.2,
    # Also record which of your own functions ran. Costs real time, so keep the
    # sample rate low when you turn this on.
    "trace_functions": False,
}

MIDDLEWARE = [
    "pulsewatch_sdk.middleware.PulseWatchMiddleware",
    # ... your other middleware
]`;
  }

  get curlSnippet(): string {
    return `curl -X POST '${this.ingestUrlFor(this.snippetPlatform)}' \\
  -H 'Content-Type: application/json' \\
  -d '{"platform":"${this.snippetPlatform}","level":"error","message":"hello from curl"}'`;
  }
}
